import { useRef, useState } from "react";

import { useOrganization } from "@asd/customer-portal-ui";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Alert, AlertTitle, Backdrop, Box, Button, Card, CircularProgress, Stack, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";

import { useKeycloak } from "~/components/authorization";
import {
  Agreement,
  Availability,
  Considerations,
  ContactInfo,
  Demographics,
  Distance,
  Experience,
  Services,
  Stepper,
  WorkPreferences,
} from "~/components/pages/provider_questionnaire";
import BooleanRadioGroup from "~/components/pages/provider_questionnaire/BooleanRadioGroup";
import { UserService } from "~/services";
import { routeToErrorPage } from "~/utils/navigationMap";
import { flattenRegistration } from "~/utils/providerRegistrationHelpers";

export const PROVIDER_REGISTRATION_STEP_ORDER = [
  { key: "contact", component: ContactInfo, title: "Contact Information" },
  { key: "demographics", component: Demographics, title: "Demographics" },
  { key: "services", component: Services, title: "Services" },
  { key: "distance", component: Distance, title: "Travel Preferences" },
  { key: "availability", component: Availability, title: "Availability" },
  { key: "workPreferences", component: WorkPreferences, title: "Work Preferences" },
  { key: "considerations", component: Considerations, title: "Considerations" },
  { key: "experience", component: Experience, title: "Experience" },
  { key: "agreement", component: Agreement, title: "Agreement" },
];

const ProviderRegistration = () => {
  const { onVerificationCallback } = useKeycloak();
  const {
    configuration: {
      branding: { name, withTM },
      supportCoordinator,
    },
  } = useOrganization();

  const formHooks = useForm({
    defaultValues: {
      publicProvider: undefined,
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = formHooks;

  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const publicProvider = watch("publicProvider");

  return (
    <Stack
      alignSelf="center"
      direction="column"
      flexGrow={1}
      justifyContent="center"
      maxWidth="lg"
      minWidth="lg"
      overflow="auto"
      padding={2}
      width="100%"
    >
      <Card elevation={6} sx={{ padding: 4, width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "auto" }}>
        <Typography align="center" color="custom.main" fontWeight="bold" mb={4} variant="h4" gutterBottom>
          {activeStep === 0 ? "Complete Registration" : PROVIDER_REGISTRATION_STEP_ORDER[activeStep - 1].title}
        </Typography>
        <FormProvider {...formHooks}>
          <Stack
            component="form"
            direction="column"
            flexGrow={1}
            height="100%"
            onSubmit={handleSubmit(async (questionResponses) => {
              setIsLoading(true);

              try {
                const linkResponse = await UserService.linkUserToProvider({
                  providerRegistration: flattenRegistration(questionResponses),
                });
                setIsLoading(false);

                if (linkResponse?.status === 204) {
                  onVerificationCallback!();
                } else {
                  routeToErrorPage();
                }
              } catch {
                routeToErrorPage();
              }
            })}
            overflow="auto"
            position="relative"
            noValidate
          >
            <Stack direction="column" flexGrow={1} height="100%" overflow="auto">
              <Box ref={scrollRef} />
              {activeStep === 0 && (
                <>
                  <Typography align="left" fontSize="1.3rem" fontWeight="bold" mb={2} gutterBottom>
                    Are you interested in having your information shared on the provider directory so that we may have{" "}
                    {supportCoordinator.title}s connect you with program participants/members for new work opportunities?
                  </Typography>
                  <Alert color="alertinfo" severity="info" sx={{ mb: 2 }}>
                    <AlertTitle fontSize="1.4rem">Disclaimer</AlertTitle>
                    <Typography>
                      Adding your information to our provider directory allows you to be searchable but does not guarantee or exclude you
                      from employment. {name}
                      {withTM && (
                        <Typography component="sup" fontSize="xx-small" variant="caption">
                          TM
                        </Typography>
                      )}{" "}
                      is not the employer. If an employer chooses to hire you, you are required to meet eligibility requirements, including
                      passing a criminal background check.
                    </Typography>
                  </Alert>
                  <BooleanRadioGroup control={control} name="publicProvider" />
                </>
              )}
              {PROVIDER_REGISTRATION_STEP_ORDER.map(
                ({ key, component: Component }, index) => activeStep === index + 1 && <Component key={key} />,
              )}
            </Stack>
            {publicProvider === true && <Stepper activeStep={activeStep} scrollRef={scrollRef} setActiveStep={setActiveStep} />}
            <Button
              disabled={
                publicProvider === undefined ||
                (publicProvider === true && (activeStep < PROVIDER_REGISTRATION_STEP_ORDER.length || Object.keys(errors).length > 0))
              }
              endIcon={<ArrowForwardIcon />}
              sx={{ mt: 3, mb: 2 }}
              type="submit"
              fullWidth
            >
              Submit
            </Button>
            {!!isLoading && (
              <>
                <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, position: "absolute" }} open />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    justifySelf: "center",
                    alignContent: "center",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                  }}
                >
                  <CircularProgress size={150} />
                </Box>
              </>
            )}
          </Stack>
        </FormProvider>
      </Card>
    </Stack>
  );
};

export default ProviderRegistration;
